import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const InHouseProject = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const labels = [
    {
      name: 'Alzaya',
      logo: '/assets/alzaya.png',
      link: 'https://soundcloud.com/alzaya',
      description: 'Independent record label and creative hub based in Milan, focused on underground electronic music through vinyl and digital releases. Blending IDM, Balearic, dreamy house, and tribal rhythms with a refined, dancefloor-driven identity.',
      logoSize: 'w-56 h-56 md:w-72 md:h-72',
    },
    {
      name: 'Unknown Milano',
      logo: '/assets/unknown-milano.png',
      link: 'https://soundcloud.com/unknownmilano',
      description: 'Independent record label based in Milan, dedicated to underground electronic and house music, supporting emerging and established artists through vinyl and digital releases.',
      logoSize: 'w-48 h-48 md:w-64 md:h-64',
    },
  ];

  return (
    <section
      id="inhouse"
      data-testid="inhouse-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-24"
        >
          <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
            Our Labels
          </span>
          <h2
            className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
            data-testid="inhouse-heading"
          >
            ORIGINAL
            <br />
            PROJECTS
          </h2>
        </motion.div>

        {/* Labels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 lg:gap-24">
          {labels.map((label, index) => (
            <motion.a
              key={label.name}
              href={label.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.8,
                delay: 0.2 + index * 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group flex flex-col items-center text-center pt-4 pb-8 px-8 md:pt-6 md:pb-12 md:px-12 border border-transparent hover:border-white/30 transition-all duration-500 ease-out"
              data-testid={`label-${label.name.toLowerCase().replace(' ', '-')}`}
            >
              {/* Logo Container */}
              <div className="relative w-48 h-48 md:w-64 md:h-64 flex items-center justify-center mb-8 transition-transform duration-500 group-hover:scale-105">
                <img
                  src={label.logo}
                  alt={`${label.name} Logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Label Name */}
              <h3 className="font-heading font-bold text-xl md:text-2xl text-white group-hover:text-white/70 transition-colors duration-300">
                {label.name}
              </h3>

              {/* Description */}
              <p className="font-body text-sm text-white/50 mt-3 max-w-sm leading-relaxed group-hover:text-white/60 transition-colors duration-300">
                {label.description}
              </p>

              {/* Underline effect */}
              <div className="w-0 h-[1px] bg-white mt-4 group-hover:w-24 transition-all duration-500 ease-out" />
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InHouseProject;
